import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

// Constellation Components
import {
    FormHelp,
    Label,
    LabeledControl,
    Radio,
    Spacer,
    Text,
} from '@zillow/constellation';

import {
    Col,
    Link,
    Row,
} from '@zillow/zrs-comet';

// analytics
import analyticsUtils from 'app/client/utils/analyticsUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';

// service
import appLogger from 'app/shared/loggers/appLogger';

// Styled Components
import {
    StyledSubTitleContainer,
} from './StyledComponents';

/** ********** static values ********** */
// props
const propTypes = {
    isApplicationsAcceptedRef: PropTypes.object.isRequired,
    errorObj: PropTypes.object.isRequired,
    isApplicationsAccepted: PropTypes.bool.isRequired,
    handleSelectionChange: PropTypes.func.isRequired,
};

// constants
const RADIO_OPTIONS = {
    YES: {
        DISPLAY: 'Yes',
        VALUE: true,
    },
    NO: {
        DISPLAY: 'No',
        VALUE: false,
    },
};

// template
const title = 'Rental applications';
const subTitle = 'Do you want to accept Zillow rental applications for this listing? ';
const copy = `Save time by allowing renters to apply directly to your listing.
    You'll receive a completed application, background check, credit report
    and eviction history – all at no cost to you.`;

const learnMore = 'Learn More';
const learnMoreUrl = 'https://www.zillow.com/marketing/rental-manager/tenant-screening/';

const EnableApplicationsRadio = (props) => {
    const {
        isApplicationsAcceptedRef,
        errorObj,
        isApplicationsAccepted,
        handleSelectionChange,
    } = props;

    // hooks
    const dispatch = useDispatch();

    // alias - error
    const { isApplicationsAccepted: errorMessage } = errorObj;

    // state
    const [selectedOption, setSelectedOption] = useState(isApplicationsAccepted);

    // methods
    /*
     * Gets the event action in the form of ToSelected-from-FromSelected
     **/
    const getEventAction = (selected) => {
        let fromSelectedDisplay = selectedOption;

        if (fromSelectedDisplay !== null) {
            fromSelectedDisplay = fromSelectedDisplay ? 'enabled' : 'disabled';
        }

        const toSelectedDisplay = selected ? 'Enable' : 'Disable';

        return `${toSelectedDisplay}-from-${fromSelectedDisplay}`;
    };

    const triggerGAEvent = (selected) => {
        const eventAction = getEventAction(selected);
        const { APPLICATIONS_RADIO_CHANGE } = AnalyticsConstants.GA_EVENTS;

        // set action
        APPLICATIONS_RADIO_CHANGE.action = eventAction;

        // log
        appLogger.logInfo({
            message: `INFO: ${JSON.stringify(APPLICATIONS_RADIO_CHANGE)}`,
            context: 'EnableApplicationsRadio.triggerGAEvent',
        });

        // trigger analytics
        dispatch(analyticsUtils.track.GAEventV2(APPLICATIONS_RADIO_CHANGE));
    };

    const handleChange = (selected) => {
        setSelectedOption(selected);
        triggerGAEvent(selected);

        // endpoint suffix, value
        handleSelectionChange('isApplicationsAccepted', selected);
    };

    // render
    const renderTitle = () => (
        <Text
            marginBottom="xs"
            fontType="h4"
            as="div"
        >
            {title}
        </Text>
    );

    const renderSubTitle = () => (
        <StyledSubTitleContainer>
            <Label
                marginBottom={0.5}
                marginRight={0.5}
                size="lg"
                required={true}
            >
                {subTitle}
                <Link
                    to={learnMoreUrl}
                    target="_blank"
                >
                    {learnMore}
                </Link>
            </Label>
        </StyledSubTitleContainer>
    );

    const renderCopy = () => (
        <Text
            marginBottom="sm"
            fontType="smallbody"
            as="div"
        >
            {copy}
        </Text>
    );

    const renderYesLabel = () => (
        <Label>
            {RADIO_OPTIONS.YES.DISPLAY}
            &nbsp;
            <b>(More common)</b>
        </Label>
    );

    const renderNoLabel = () => (
        <Label>
            {RADIO_OPTIONS.NO.DISPLAY}
            &nbsp;
            <b>(Less common)&nbsp;</b>
    - I will manually collect all application data from renters and run credit and criminal checks separately.
        </Label>
    );

    // Known error: Failed prop type: Invalid prop `label` of type `object` supplied to `Radio`, expected `string`
    // renderYesLabel returns jsx in order to style part of the label
    const renderRadioOptions = () => (
        <Spacer marginBottom="sm">
            <Row>
                <Col>
                    <div>
                        <LabeledControl
                            controlId="LabeledControl-EnableApplicationsRadio1"
                            label={renderYesLabel()}
                            control={(
                                <Radio
                                    name="EnableApplicationsRadio"
                                    checked={selectedOption === RADIO_OPTIONS.YES.VALUE}
                                    onChange={() => handleChange(RADIO_OPTIONS.YES.VALUE)}
                                />
                            )}
                        />
                        <LabeledControl
                            controlId="LabeledControl-EnableApplicationsRadio2"
                            label={renderNoLabel()}
                            control={(
                                <Radio
                                    name="EnableApplicationsRadio"
                                    checked={selectedOption === RADIO_OPTIONS.NO.VALUE}
                                    onChange={() => handleChange(RADIO_OPTIONS.NO.VALUE)}
                                />
                            )}
                        />
                    </div>
                    <Spacer marginY="lg" />
                </Col>
            </Row>
        </Spacer>
    );

    const renderErrorMessage = () => (
        <FormHelp
            error={true}
        >
            {errorMessage}
        </FormHelp>
    );

    return (
        <div className="EnableApplicationRadio">
            <Spacer marginBottom="xl">
                <div ref={isApplicationsAcceptedRef} />
                {renderTitle()}
                {renderSubTitle()}
                {renderCopy()}
                {renderRadioOptions()}
                {errorMessage && renderErrorMessage()}
            </Spacer>
        </div>
    );
};

EnableApplicationsRadio.propTypes = propTypes;

export { EnableApplicationsRadio };
export default EnableApplicationsRadio;
